import React, { useEffect, useState } from 'react';
import { paymentsAPI, salesOrdersAPI, salesAPI, salesInvoicesAPI, clientsAPI, storesAPI } from '../services/api';
import { ModernCard, ModernTable, ModernBadge, ModernInput, ModernSelect, ModernLoadingSpinner, ModernButton, ModernSearchBar, ModernModal, ModernStatCard } from '../components/ui/ModernComponents';
import { formatCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import { Chart, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Plus, Filter, User, CreditCard, FileText, Calendar, BadgeCheck, BadgeX, AlertCircle, Coins, CheckCircle, Banknote, Edit } from 'lucide-react';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
Chart.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const paymentModes = [
  { value: '', label: 'Tous' },
  { value: 'espece', label: 'Espèce' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'carte_bancaire', label: 'Carte bancaire' }
];
const referenceTypes = [
  { value: '', label: 'Tous' },
  { value: 'sales_order', label: 'Commande' },
  { value: 'sale', label: 'Vente directe' },
  { value: 'sales_invoice', label: 'Facture' }
];

const getStatus = (paid, total) => {
  if (!total) return { label: 'Inconnu', color: 'neutral' };
  if (paid >= total) return { label: 'Payé', color: 'success' };
  if (paid > 0 && paid < total) return { label: 'Partiel', color: 'warning' };
  return { label: 'Non payé', color: 'danger' };
};

const Paiements = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ reference_type: '', mode: '', start_date: '', end_date: '', cheque_number: '' });
  const [relatedTotals, setRelatedTotals] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editPayment, setEditPayment] = useState({
    reference_type: '',
    reference_id: '',
    client_id: '',
    amount: '',
    mode: 'espece',
    cheque_number: '',
    note: '',
    payment_date: '',
    store_id: '',
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [amountRange, setAmountRange] = useState({ min: '', max: '' });
  const [summary, setSummary] = useState({ total: 0, paid: 0, partial: 0, unpaid: 0, byType: {}, byMode: {} });
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPayment, setAddPayment] = useState({
    reference_type: '',
    reference_id: '',
    client_id: '',
    amount: '',
    mode: 'espece',
    cheque_number: '',
    note: '',
    payment_date: new Date().toISOString().slice(0, 10),
    store_id: user?.role === 'admin' ? '' : undefined,
  });
  const [referenceOptions, setReferenceOptions] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // 1. Add state for edit modal
  const [showEditModal, setShowEditModal] = useState(false);

  // Calculate stats
  const today = new Date().toISOString().slice(0, 10);
  const totalPayments = payments.length;
  const totalPaid = payments.filter(p => Number(p.amount) > 0).reduce((sum, p) => sum + Number(p.amount), 0);
  const totalUnpaid = payments.filter(p => Number(p.amount) === 0).length;
  const todayPayments = payments.filter(p => (p.payment_date || '').slice(0, 10) === today);
  const todayCount = todayPayments.length;
  const todayAmount = todayPayments.reduce((sum, p) => sum + Number(p.amount), 0);
  const countEsp = payments.filter(p => p.mode === 'espece').length;
  const countCheque = payments.filter(p => p.mode === 'cheque').length;
  const countCard = payments.filter(p => p.mode === 'carte_bancaire').length;
  const amountEsp = payments.filter(p => p.mode === 'espece').reduce((sum, p) => sum + Number(p.amount), 0);
  const amountCheque = payments.filter(p => p.mode === 'cheque').reduce((sum, p) => sum + Number(p.amount), 0);
  const amountCard = payments.filter(p => p.mode === 'carte_bancaire').reduce((sum, p) => sum + Number(p.amount), 0);

  // Fetch references for dropdown when type changes
  useEffect(() => {
    const fetchRefs = async () => {
      if (addPayment.reference_type === 'sales_order') {
        const res = await salesOrdersAPI.getSalesOrders();
        const orders = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setReferenceOptions(orders.map(o => ({ value: o.id, label: `Commande #${o.id}` })));
      } else if (addPayment.reference_type === 'sales_invoice') {
        const res = await salesInvoicesAPI.getSalesInvoices();
        setReferenceOptions(res.data.map(i => ({ value: i.id, label: `Facture #${i.id}` })));
      } else if (addPayment.reference_type === 'sale') {
        const res = await salesAPI.getSales();
        setReferenceOptions(res.data.map(s => ({ value: s.id, label: `Vente #${s.id}` })));
      } else {
        setReferenceOptions([]);
      }
    };
    if (addPayment.reference_type) fetchRefs();
  }, [addPayment.reference_type]);

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      const res = await clientsAPI.getClients();
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setClients([]);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [filters]);

  useEffect(() => {
    if (user?.role === 'admin') {
      storesAPI.getStores().then(res => setStores(res.data)).catch(() => setStores([]));
    }
  }, [user]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await paymentsAPI.exportPaymentsJson(filters);
      const data = res.data;
      // Advanced search/filter
      let filtered = data;
      if (search) filtered = filtered.filter(p => (p.note || '').toLowerCase().includes(search.toLowerCase()));
      if (filters.client_id) filtered = filtered.filter(p => p.client_id === filters.client_id);
      if (amountRange.min) filtered = filtered.filter(p => Number(p.amount) >= Number(amountRange.min));
      if (amountRange.max) filtered = filtered.filter(p => Number(p.amount) <= Number(amountRange.max));
      if (filters.cheque_number) filtered = filtered.filter(p => (p.cheque_number || '').includes(filters.cheque_number));
      setPayments(filtered);
      // Use reference_total directly from backend
      const totals = {};
      let paid = 0, partial = 0, unpaid = 0, total = 0;
      const byType = {}, byMode = {};
      filtered.forEach((p) => {
        const t = Number(p.reference_total) || null;
        totals[`${p.reference_type}_${p.reference_id}`] = t;
        const paidAmt = Number(p.amount);
        total += paidAmt;
        if (t) {
          if (paidAmt >= t) paid++;
          else if (paidAmt > 0) partial++;
          else unpaid++;
        }
        byType[p.reference_type] = (byType[p.reference_type] || 0) + paidAmt;
        byMode[p.mode] = (byMode[p.mode] || 0) + paidAmt;
      });
      setRelatedTotals(totals);
      setSummary({ total, paid, partial, unpaid, byType, byMode });
    } catch {
      setPayments([]);
      setRelatedTotals({});
      setSummary({ total: 0, paid: 0, partial: 0, unpaid: 0, byType: {}, byMode: {} });
    }
    setLoading(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const startEdit = async (payment) => {
    setEditingId(payment.id);
    setEditPayment({
      reference_type: payment.reference_type || '',
      reference_id: payment.reference_id || '',
      client_id: payment.client_id || '',
      amount: payment.amount,
      mode: payment.mode,
      cheque_number: payment.cheque_number || '',
      note: payment.note || '',
      payment_date: payment.payment_date ? payment.payment_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      store_id: payment.store_id || (user?.role === 'admin' ? '' : undefined),
    });
    // Update referenceOptions for edit mode
    if (payment.reference_type === 'sales_order') {
      const res = await salesOrdersAPI.getSalesOrders();
      const orders = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setReferenceOptions(orders.map(o => ({ value: o.id, label: `Commande #${o.id}` })));
    } else if (payment.reference_type === 'sales_invoice') {
      const res = await salesInvoicesAPI.getSalesInvoices();
      setReferenceOptions(res.data.map(i => ({ value: i.id, label: `Facture #${i.id}` })));
    } else if (payment.reference_type === 'sale') {
      const res = await salesAPI.getSales();
      setReferenceOptions(res.data.map(s => ({ value: s.id, label: `Vente #${s.id}` })));
    } else {
      setReferenceOptions([]);
    }
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditPayment({ reference_type: '', reference_id: '', client_id: '', amount: '', mode: 'espece', cheque_number: '', note: '', payment_date: '', store_id: user?.role === 'admin' ? '' : undefined });
  };
  const handleSaveEdit = async (id) => {
    setActionLoading(true);
    try {
      await paymentsAPI.updatePayment(id, editPayment);
      setEditingId(null);
      setEditPayment({ reference_type: '', reference_id: '', client_id: '', amount: '', mode: 'espece', cheque_number: '', note: '', payment_date: '', store_id: user?.role === 'admin' ? '' : undefined });
      fetchPayments();
    } catch {}
    setActionLoading(false);
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    setActionLoading(true);
    try {
      await paymentsAPI.deletePayment(id);
      fetchPayments();
    } catch {}
    setActionLoading(false);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await paymentsAPI.createPayment({
        reference_type: addPayment.reference_type,
        reference_id: addPayment.reference_id,
        client_id: addPayment.client_id,
        amount: addPayment.amount,
        mode: addPayment.mode,
        cheque_number: addPayment.mode === 'cheque' ? addPayment.cheque_number : undefined,
        note: addPayment.note,
        payment_date: addPayment.payment_date,
        store_id: user?.role === 'admin' ? addPayment.store_id : undefined,
      });
      setShowAddModal(false);
      setAddPayment({ reference_type: '', reference_id: '', client_id: '', amount: '', mode: 'espece', cheque_number: '', note: '', payment_date: new Date().toISOString().slice(0, 10), store_id: user?.role === 'admin' ? '' : undefined });
      fetchPayments();
    } catch {
      // handle error
    }
    setAddLoading(false);
  };

  // Bulk actions
  const handleSelect = (id, checked) => {
    setSelectedIds(ids => checked ? [...ids, id] : ids.filter(i => i !== id));
  };
  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? payments.map(p => p.id) : []);
  };
  const handleBulkDelete = async () => {
    if (!window.confirm('Supprimer les paiements sélectionnés ?')) return;
    setActionLoading(true);
    try {
      await Promise.all(selectedIds.map(id => paymentsAPI.deletePayment(id)));
      setSelectedIds([]);
      fetchPayments();
    } catch {}
    setActionLoading(false);
  };
  const handleBulkExport = async () => {
    // Export selected payments as CSV
    // For demo, just filter and download client-side
    const selected = payments.filter(p => selectedIds.includes(p.id));
    const csv = [Object.keys(selected[0] || {}).join(',')].concat(selected.map(p => Object.values(p).join(','))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'paiements_selection.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Add client and store columns
  const columns = [
    { key: 'select', label: <input type="checkbox" checked={selectedIds.length === payments.length && payments.length > 0} onChange={e => handleSelectAll(e.target.checked)} />, render: (v, p) => (
      <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={e => handleSelect(p.id, e.target.checked)} />
    ) },
    { key: 'id', label: 'ID' },
    ...(user?.role === 'admin' ? [{ key: 'store_id', label: 'Magasin', render: (v) => {
      const store = stores.find(s => String(s.id) === String(v));
      return store ? store.name : v;
    }}] : []),
    { key: 'client', label: 'Client', render: (v, p) => p.client_name || '-' },
    { key: 'reference_type', label: 'Type', render: (v, p) => p.reference_type_label || v },
    { key: 'reference_id', label: 'Référence', render: (v, p) => (
      <a href={`/${p.reference_type === 'sales_order' ? 'ventes/commandes' : p.reference_type === 'sale' ? 'ventes' : 'ventes/factures'}/${v}`} target="_blank" rel="noopener noreferrer">{v}</a>
    ) },
    { key: 'amount', label: 'Montant', render: (v, p) => (
      editingId === p.id ? <ModernInput type="number" value={editPayment.amount} onChange={e => setEditPayment(ep => ({ ...ep, amount: e.target.value }))} /> : formatCurrency(v)
    ) },
    { key: 'mode', label: 'Mode', render: (v, p) => (
      editingId === p.id ? <ModernSelect value={editPayment.mode} onChange={e => setEditPayment(ep => ({ ...ep, mode: e.target.value }))} options={paymentModes} /> : v
    ) },
    { key: 'cheque_number', label: 'N° Chèque', render: (v, p) => (
      p.mode === 'cheque' ? (
        editingId === p.id
          ? <ModernInput value={editPayment.cheque_number} onChange={e => setEditPayment(ep => ({ ...ep, cheque_number: e.target.value }))} />
          : v || <span className="text-neutral-400">-</span>
      ) : <span className="text-neutral-300">-</span>
    ) },
    { key: 'payment_date', label: 'Date' },
    { key: 'note', label: 'Note', render: (v, p) => (
      editingId === p.id ? <ModernInput value={editPayment.note} onChange={e => setEditPayment(ep => ({ ...ep, note: e.target.value }))} /> : v
    ) },
    { key: 'status', label: 'Statut', render: (v, p) => {
      const total = relatedTotals[`${p.reference_type}_${p.reference_id}`];
      const paid = Number(p.amount);
      const status = getStatus(paid, total);
      return <ModernBadge variant={status.color}>{status.label}</ModernBadge>;
    } },
    { key: 'remaining', label: 'Reste à payer', render: (v, p) => {
      const total = relatedTotals[`${p.reference_type}_${p.reference_id}`];
      const paid = Number(p.amount);
      return total ? formatCurrency(Math.max(0, total - paid)) : '-';
    } },
    { key: 'actions', label: 'Actions', render: (v, p) => (
      <>
        <ModernButton size="sm" variant="primary" icon={<Edit className="w-4 h-4" />} onClick={() => { startEdit(p); setShowEditModal(true); }}>Modifier</ModernButton>
        <ModernButton size="sm" variant="danger" onClick={() => handleDelete(p.id)} disabled={actionLoading}>Supprimer</ModernButton>
      </>
    ) }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernStatCard title="Total Paiements" value={totalPayments} icon={<Coins className="w-7 h-7 text-primary-600" />} variant="primary" />
        <ModernStatCard title="Total Payé" value={formatCurrency(totalPaid)} icon={<CheckCircle className="w-7 h-7 text-success-600" />} variant="success" />
        <ModernStatCard title="Non Payés" value={totalUnpaid} icon={<AlertCircle className="w-7 h-7 text-danger-600" />} variant="danger" />
        <ModernStatCard title="Aujourd'hui" value={`${todayCount} / ${formatCurrency(todayAmount)}`} icon={<Calendar className="w-7 h-7 text-info-600" />} variant="info" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-2">
        <ModernStatCard title="Espèce" value={`${countEsp} / ${formatCurrency(amountEsp)}`} icon={<Banknote className="w-7 h-7 text-success-600" />} variant="success" />
        <ModernStatCard title="Chèque" value={`${countCheque} / ${formatCurrency(amountCheque)}`} icon={<CreditCard className="w-7 h-7 text-info-600" />} variant="info" />
        <ModernStatCard title="Carte Bancaire" value={`${countCard} / ${formatCurrency(amountCard)}`} icon={<CreditCard className="w-7 h-7 text-primary-600" />} variant="primary" />
      </div>
      <ModernCard className="p-0 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <CreditCard className="w-7 h-7 text-primary-600" />
            <h1 className="text-2xl font-bold text-neutral-900">Gestion des Paiements</h1>
          </div>
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <ModernButton variant="ghost" icon={<Filter className="w-5 h-5" />} onClick={() => setShowFilters(f => !f)}>
              Filtres
            </ModernButton>
            <ModernButton variant="primary" icon={<Plus className="w-5 h-5" />} onClick={() => setShowAddModal(true)}>
              Ajouter un paiement
            </ModernButton>
          </div>
        </div>
        {showFilters && (
          <div className="p-6 bg-neutral-50 border-b animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <ModernSelect label="Type" name="reference_type" value={filters.reference_type} onChange={handleFilterChange} options={referenceTypes} icon={<FileText className="w-4 h-4 text-primary-400" />} />
              <ModernSelect label="Mode" name="mode" value={filters.mode} onChange={handleFilterChange} options={paymentModes} icon={<CreditCard className="w-4 h-4 text-primary-400" />} />
              <ModernInput label="Date début" name="start_date" type="date" value={filters.start_date} onChange={handleFilterChange} icon={<Calendar className="w-4 h-4 text-primary-400" />} />
              <ModernInput label="Date fin" name="end_date" type="date" value={filters.end_date} onChange={handleFilterChange} icon={<Calendar className="w-4 h-4 text-primary-400" />} />
              <ModernInput label="N° Chèque" name="cheque_number" value={filters.cheque_number || ''} onChange={handleFilterChange} icon={<CreditCard className="w-4 h-4 text-primary-400" />} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
              <ModernSearchBar placeholder="Recherche note..." onSearch={setSearch} debounce={300} />
              <ModernSelect label="Client" name="client_id" value={filters.client_id} onChange={handleFilterChange} options={[{value:'',label:'Tous'},...clients.map(c=>({value:c.id,label:c.name}))]} icon={<User className="w-4 h-4 text-primary-400" />} />
              <ModernInput label="Montant min" type="number" value={amountRange.min} onChange={e => setAmountRange(r=>({...r,min:e.target.value}))} />
              <ModernInput label="Montant max" type="number" value={amountRange.max} onChange={e => setAmountRange(r=>({...r,max:e.target.value}))} />
            </div>
          </div>
        )}
        <div className="p-6 overflow-x-auto">
          <ModernTable
            columns={columns.map(col => {
              if (col.key === 'mode') {
                return { ...col, render: (v, p) => (
                  <span className="flex items-center gap-1">
                    {v === 'espece' && <CreditCard className="w-4 h-4 text-success-500" />}
                    {v === 'cheque' && <CreditCard className="w-4 h-4 text-info-500" />}
                    {v === 'carte_bancaire' && <CreditCard className="w-4 h-4 text-primary-500" />}
                    <span>{v}</span>
                  </span>
                ) };
              }
              if (col.key === 'status') {
                return { ...col, render: (v, p) => {
                  const total = relatedTotals[`${p.reference_type}_${p.reference_id}`];
                  const paid = Number(p.amount);
                  const status = getStatus(paid, total);
                  return (
                    <span className="flex items-center gap-1">
                      {status.color === 'success' && <BadgeCheck className="w-4 h-4 text-success-500" />}
                      {status.color === 'danger' && <BadgeX className="w-4 h-4 text-danger-500" />}
                      {status.color === 'warning' && <AlertCircle className="w-4 h-4 text-warning-500" />}
                      <ModernBadge variant={status.color}>{status.label}</ModernBadge>
                    </span>
                  );
                } };
              }
              return col;
            })}
            data={payments}
            className="w-full"
            rowClassName="hover:bg-neutral-50 even:bg-neutral-50"
          />
        </div>
      </ModernCard>
      <ModernModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Ajouter un paiement" size="md">
        <form onSubmit={handleAddPayment} className="space-y-4">
          <ModernSelect
            label="Type"
            name="reference_type"
            value={addPayment.reference_type}
            onChange={e => setAddPayment(p => ({ ...p, reference_type: e.target.value, reference_id: '' }))}
            options={referenceTypes.filter(rt => rt.value)}
            required
          />
          <ModernSelect
            label="Référence"
            name="reference_id"
            value={addPayment.reference_id}
            onChange={e => setAddPayment(p => ({ ...p, reference_id: e.target.value }))}
            options={referenceOptions}
            required
          />
          {/* Replace ModernSelect for Client with EntitySelectWithAdd in Add Payment Modal */}
          <EntitySelectWithAdd
            entityType="client"
            label="Client"
            options={clients.map(c => ({ value: c.id, label: c.name }))}
            value={addPayment.client_id}
            onChange={value => setAddPayment(p => ({ ...p, client_id: value }))}
            fetchOptions={fetchClients}
            required
            showAddButton={true}
          />
          {user?.role === 'admin' && (
            <ModernSelect
              label="Magasin"
              name="store_id"
              value={addPayment.store_id}
              onChange={e => setAddPayment(p => ({ ...p, store_id: e.target.value }))}
              options={[{ value: '', label: 'Sélectionner un magasin' }, ...stores.map(s => ({ value: s.id, label: s.name }))]}
              required
            />
          )}
          <ModernInput
            label="Montant"
            name="amount"
            type="number"
            min="0.01"
            step="0.01"
            value={addPayment.amount}
            onChange={e => setAddPayment(p => ({ ...p, amount: e.target.value }))}
            required
          />
          <ModernSelect
            label="Mode"
            name="mode"
            value={addPayment.mode}
            onChange={e => setAddPayment(p => ({ ...p, mode: e.target.value }))}
            options={paymentModes.filter(pm => pm.value)}
            required
          />
          {addPayment.mode === 'cheque' && (
            <ModernInput
              label="Numéro de chèque"
              name="cheque_number"
              value={addPayment.cheque_number}
              onChange={e => setAddPayment(p => ({ ...p, cheque_number: e.target.value }))}
              required
            />
          )}
          <ModernInput
            label="Note"
            name="note"
            value={addPayment.note}
            onChange={e => setAddPayment(p => ({ ...p, note: e.target.value }))}
          />
          <ModernInput
            label="Date"
            name="payment_date"
            type="date"
            value={addPayment.payment_date}
            onChange={e => setAddPayment(p => ({ ...p, payment_date: e.target.value }))}
            required
          />
          <div className="flex justify-end">
            <ModernButton type="button" variant="secondary" onClick={() => setShowAddModal(false)} className="mr-2">Annuler</ModernButton>
            <ModernButton type="submit" variant="primary" loading={addLoading}>Ajouter</ModernButton>
          </div>
        </form>
      </ModernModal>
      <ModernModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setEditingId(null); }} title="Modifier le paiement" size="md">
        <form onSubmit={e => { e.preventDefault(); handleSaveEdit(editingId); setShowEditModal(false); }} className="space-y-4">
          <ModernSelect
            label="Type"
            name="reference_type"
            value={editPayment.reference_type}
            onChange={async e => {
              const type = e.target.value;
              setEditPayment(ep => ({ ...ep, reference_type: type, reference_id: '' }));
              // Update referenceOptions for new type
              if (type === 'sales_order') {
                const res = await salesOrdersAPI.getSalesOrders();
                const orders = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setReferenceOptions(orders.map(o => ({ value: o.id, label: `Commande #${o.id}` })));
              } else if (type === 'sales_invoice') {
                const res = await salesInvoicesAPI.getSalesInvoices();
                setReferenceOptions(res.data.map(i => ({ value: i.id, label: `Facture #${i.id}` })));
              } else if (type === 'sale') {
                const res = await salesAPI.getSales();
                setReferenceOptions(res.data.map(s => ({ value: s.id, label: `Vente #${s.id}` })));
              } else {
                setReferenceOptions([]);
              }
            }}
            options={referenceTypes.filter(rt => rt.value)}
            required
          />
          <ModernSelect
            label="Référence"
            name="reference_id"
            value={editPayment.reference_id}
            onChange={e => setEditPayment(ep => ({ ...ep, reference_id: e.target.value }))}
            options={referenceOptions}
            required
          />
          {/* Replace ModernSelect for Client with EntitySelectWithAdd in Edit Payment Modal */}
          <EntitySelectWithAdd
            entityType="client"
            label="Client"
            options={clients.map(c => ({ value: c.id, label: c.name }))}
            value={editPayment.client_id}
            onChange={value => setEditPayment(ep => ({ ...ep, client_id: value }))}
            fetchOptions={fetchClients}
            required
            showAddButton={true}
          />
          {user?.role === 'admin' && (
            <ModernSelect
              label="Magasin"
              name="store_id"
              value={editPayment.store_id}
              onChange={e => setEditPayment(ep => ({ ...ep, store_id: e.target.value }))}
              options={stores.map(s => ({ value: s.id, label: s.name }))}
              required
            />
          )}
          <ModernInput
            label="Montant"
            name="amount"
            type="number"
            min="0.01"
            step="0.01"
            value={editPayment.amount}
            onChange={e => setEditPayment(ep => ({ ...ep, amount: e.target.value }))}
            required
          />
          <ModernSelect
            label="Mode"
            name="mode"
            value={editPayment.mode}
            onChange={e => setEditPayment(ep => ({ ...ep, mode: e.target.value }))}
            options={paymentModes.filter(pm => pm.value)}
            required
          />
          {editPayment.mode === 'cheque' && (
            <ModernInput
              label="Numéro de chèque"
              name="cheque_number"
              value={editPayment.cheque_number}
              onChange={e => setEditPayment(ep => ({ ...ep, cheque_number: e.target.value }))}
              required
            />
          )}
          <ModernInput
            label="Note"
            name="note"
            value={editPayment.note}
            onChange={e => setEditPayment(ep => ({ ...ep, note: e.target.value }))}
          />
          <ModernInput
            label="Date"
            name="payment_date"
            type="date"
            value={editPayment.payment_date}
            onChange={e => setEditPayment(ep => ({ ...ep, payment_date: e.target.value }))}
            required
          />
          <div className="flex justify-end">
            <ModernButton type="button" variant="secondary" onClick={() => { setShowEditModal(false); setEditingId(null); }} className="mr-2">Annuler</ModernButton>
            <ModernButton type="submit" variant="primary" loading={actionLoading}>Enregistrer</ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Paiements; 